import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { patientPaymentMethods } from '../models/patientPaymentMethods';
import { PatientPaymentMethod } from '../types/PatientPaymentMethod';
import { eq, and } from 'drizzle-orm';
import { BaseRepository } from '../../../classes/BaseRepository';
import { convertToAppError, AppError, AsyncResult, err, ok } from '@shared/utils';
import { HTTP_STATUS_NAMES, DATA_API_MESSAGES } from '@shared/constants';
import { PAYMENT_METHOD_TYPE } from '../constants/PAYMENT_METHOD_TYPE';

/**
 * @group Patient
 * @summary Patient payment method repository
 * 
 * <blockquote>
 * Provides data access methods for patient payment method management, including CRUD operations and default payment method logic.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods for payment method management.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const paymentMethodRepository = new PatientPaymentMethodRepository();
 * const paymentMethods = await paymentMethodRepository.getPaymentMethodsByUserId('user-uuid');
 * const defaultMethod = await paymentMethodRepository.getDefaultPaymentMethodByUserId('user-uuid');
 * ```
 */
export class PatientPaymentMethodRepository extends BaseRepository<PatientPaymentMethod> {
  protected table = patientPaymentMethods;

  /**
   * Get payment methods by user ID
   * 
   * @param userId - The user ID to get payment methods for
   * @returns AsyncResult containing payment methods array or error
   */
  async getPaymentMethodsByUserId(userId: string): AsyncResult<PatientPaymentMethod[], AppError> {
    if (!userId) {
      return err(convertToAppError(
        'User ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table).where(eq(this.table.user_id, userId))
        );

        return result.match({
          ok: (rows) => ok(rows),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get default payment method for a user
   * 
   * @param userId - The user ID to get default payment method for
   * @returns AsyncResult containing default payment method or error
   */
  async getDefaultPaymentMethodByUserId(userId: string): AsyncResult<PatientPaymentMethod | null, AppError> {
    if (!userId) {
      return err(convertToAppError(
        'User ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table)
            .where(and(eq(this.table.user_id, userId), eq(this.table.is_default, true)))
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return ok(null);
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Create a new payment method with default selection logic
   * 
   * @param data - Payment method data
   * @returns AsyncResult containing created payment method or error
   */
  async create(data: Partial<PatientPaymentMethod>): AsyncResult<PatientPaymentMethod, AppError> {
    // Validate required fields
    const validationError = this.validatePaymentMethodData(data);
    if (validationError) {
      return err(validationError);
    }

    // If this is being set as default, unset other defaults for the user
    if (data.is_default && data.user_id) {
      const unsetResult = await this.unsetDefaultPaymentMethodsForUser(data.user_id);
      if (unsetResult.isErr) {
        return err(unsetResult.getFail());
      }
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.insert(this.table).values(data as any).returning()
        );

        return result.match({
          ok: (rows) => ok(rows[0]),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Update payment method with default selection logic
   * 
   * @param id - Payment method ID to update
   * @param data - Updated payment method data
   * @returns AsyncResult containing updated payment method or error
   */
  async update(id: string, data: Partial<PatientPaymentMethod>): AsyncResult<PatientPaymentMethod, AppError> {
    if (!id) {
      return err(convertToAppError(
        'Payment method ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    // Validate data if provided
    if (Object.keys(data).length > 0) {
      const validationError = this.validatePaymentMethodData(data, true);
      if (validationError) {
        return err(validationError);
      }
    }

    // If this is being set as default, unset other defaults for the user
    if (data.is_default) {
      // Get the current payment method to find the user_id
      const currentMethodResult = await this.getById(id);
      if (currentMethodResult.isErr) {
        return err(currentMethodResult.getFail());
      }
      
      const currentMethod = currentMethodResult.getSuccess();
      const unsetResult = await this.unsetDefaultPaymentMethodsForUser(currentMethod.user_id);
      if (unsetResult.isErr) {
        return err(unsetResult.getFail());
      }
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.update(this.table)
            .set({
              ...data,
              date_modified: new Date()
            })
            .where(eq(this.table.id, id))
            .returning()
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return err(convertToAppError(
                `Payment method not found with id: ${id}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.NOT_FOUND
              ));
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Set a payment method as default (unset others for the user)
   * 
   * @param paymentMethodId - Payment method ID to set as default
   * @returns AsyncResult containing updated payment method or error
   */
  async setAsDefault(paymentMethodId: string): AsyncResult<PatientPaymentMethod, AppError> {
    if (!paymentMethodId) {
      return err(convertToAppError(
        'Payment method ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    // Get the current payment method to find the user_id
    const currentMethodResult = await this.getById(paymentMethodId);
    if (currentMethodResult.isErr) {
      return err(currentMethodResult.getFail());
    }

    const currentMethod = currentMethodResult.getSuccess();

    // Unset other defaults for the user
    const unsetResult = await this.unsetDefaultPaymentMethodsForUser(currentMethod.user_id);
    if (unsetResult.isErr) {
      return err(unsetResult.getFail());
    }

    // Set this payment method as default
    return this.update(paymentMethodId, { is_default: true });
  }

  /**
   * Unset all default payment methods for a user
   * 
   * @param userId - User ID to unset defaults for
   * @returns AsyncResult containing success or error
   */
  private async unsetDefaultPaymentMethodsForUser(userId: string): AsyncResult<boolean, AppError> {
    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.update(this.table)
            .set({
              is_default: false,
              date_modified: new Date()
            })
            .where(and(eq(this.table.user_id, userId), eq(this.table.is_default, true)))
        );

        return result.match({
          ok: () => ok(true),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get payment methods by type for a user
   * 
   * @param userId - User ID to filter by
   * @param paymentMethodType - Payment method type to filter by
   * @returns AsyncResult containing payment methods array or error
   */
  async getPaymentMethodsByType(userId: string, paymentMethodType: string): AsyncResult<PatientPaymentMethod[], AppError> {
    if (!userId) {
      return err(convertToAppError(
        'User ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    if (!paymentMethodType) {
      return err(convertToAppError(
        'Payment method type is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table)
            .where(and(eq(this.table.user_id, userId), eq(this.table.payment_method_type, paymentMethodType)))
        );

        return result.match({
          ok: (rows) => ok(rows),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Validate payment method data
   * 
   * @param data - Payment method data to validate
   * @param isUpdate - Whether this is an update operation (allows partial data)
   * @returns AppError if validation fails, null if valid
   */
  private validatePaymentMethodData(data: Partial<PatientPaymentMethod>, isUpdate: boolean = false): AppError | null {
    // Required fields for creation
    if (!isUpdate) {
      if (!data.user_id) {
        return convertToAppError(
          'User ID is required',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
      if (!data.payment_method_type) {
        return convertToAppError(
          'Payment method type is required',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
      if (!data.account_holder_name) {
        return convertToAppError(
          'Account holder name is required',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
      if (!data.last_four_digits) {
        return convertToAppError(
          'Last four digits is required',
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
    }

    // Field length validation
    if (data.account_holder_name && data.account_holder_name.length > 64) {
      return convertToAppError(
        'Account holder name must be 64 characters or less',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      );
    }
    if (data.last_four_digits && data.last_four_digits.length > 4) {
      return convertToAppError(
        'Last four digits must be 4 characters or less',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      );
    }
    if (data.bank_name && data.bank_name.length > 64) {
      return convertToAppError(
        'Bank name must be 64 characters or less',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      );
    }

    // Payment method type validation
    if (data.payment_method_type) {
      const validTypes = Object.values(PAYMENT_METHOD_TYPE);
      if (!validTypes.includes(data.payment_method_type as any)) {
        return convertToAppError(
          `Invalid payment method type. Must be one of: ${validTypes.join(', ')}`,
          DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
          HTTP_STATUS_NAMES.BAD_REQUEST
        );
      }
    }

    // Last four digits format validation
    if (data.last_four_digits && !/^\d{4}$/.test(data.last_four_digits)) {
      return convertToAppError(
        'Last four digits must be exactly 4 digits',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      );
    }

    return null;
  }

  /**
   * Find payment methods by Paystack customer code
   * 
   * @param customerCode - Paystack customer code to search for
   * @returns AsyncResult containing payment methods array or error
   */
  async findByCustomerCode(customerCode: string): AsyncResult<PatientPaymentMethod[], AppError> {
    if (!customerCode) {
      return err(convertToAppError(
        'Customer code is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table).where(eq(this.table.paystack_customer_code, customerCode))
        );

        return result.match({
          ok: (rows) => ok(rows),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Find payment method by Paystack authorization code
   * 
   * @param authorizationCode - Paystack authorization code to search for
   * @returns AsyncResult containing payment method or null if not found
   */
  async findByAuthorizationCode(authorizationCode: string): AsyncResult<PatientPaymentMethod | null, AppError> {
    if (!authorizationCode) {
      return err(convertToAppError(
        'Authorization code is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table).where(eq(this.table.paystack_authorization_code, authorizationCode))
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return ok(null);
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }
}
